import type { FacilityHierarchyInput } from "@utils/facilityUtils";
import { getFacilityHierarchyId } from "@utils/facilityUtils";
import type { MyFacilityListRow } from "types/myFacilityListRow";
import type {
  FqhcDashboardData,
  FqhcDashboardFacility,
  FqhcSourceProgram,
  NetworkDashboardMeta,
} from "@services/networkDashboard";
import {
  abbreviateFacilityType,
  facilityLocation,
  networkRegistryDisplayIds,
} from "./facilityNetworkChildrenUtils";

export const FQHC_TYPE_CODE = "FQHC";

export const FQHC_DASHBOARD_SUBTITLE =
  "Public HRSA UDS, CHQR, grants, FTCA, 340B, and active service-site data";

export const FQHC_ROSTER_SUBTITLE =
  "FQHC-specific parameters · click a facility to view its dashboard";

export type FqhcTone = "error" | "warn" | "ok" | "muted" | "neutral";

export type FqhcKpiTile = {
  id: string;
  label: string;
  value: string;
  sublabel: string;
  valueTone?: FqhcTone;
};

export type FqhcLiveRosterRow = FqhcDashboardFacility & {
  key: string;
  dashboardFacility: MyFacilityListRow | null;
};

export type FqhcLiveDashboardModel = {
  facilityCount: number;
  kpis: FqhcKpiTile[];
  roster: FqhcLiveRosterRow[];
};

function toDashboardFacility(
  facility: FacilityHierarchyInput,
  facilityId: number,
): MyFacilityListRow {
  return {
    facility_id: facilityId,
    id: facilityId,
    facility_name: facility.facility_name || `Facility ${facilityId}`,
    city: typeof facility.city === "string" ? facility.city : "",
    state: typeof facility.state === "string" ? facility.state : "",
    full_address: facilityLocation(facility),
    facility_type:
      (typeof facility.facility_type === "string" && facility.facility_type) || "FQHC",
    facility_type_slug:
      (typeof facility.facility_type_slug === "string" && facility.facility_type_slug) ||
      "federally_qualified_health_center",
    org_id: 0,
    org_name: "",
  };
}

export function collectLinkedFqhcFacilities(
  members: FacilityHierarchyInput[],
  selectedId: number | null,
): FacilityHierarchyInput[] {
  return members.filter((member) => {
    const id = getFacilityHierarchyId(member);
    if (selectedId != null && id === selectedId) return false;
    return abbreviateFacilityType(member) === FQHC_TYPE_CODE;
  });
}

export function buildFqhcMembershipSignature(
  members: FacilityHierarchyInput[],
): string {
  return members
    .filter((member) => abbreviateFacilityType(member) === FQHC_TYPE_CODE)
    .map((member) => {
      const facilityId = getFacilityHierarchyId(member);
      const ccn = networkRegistryDisplayIds(member).ccn;
      return `${facilityId ?? "no-id"}:${ccn ?? ""}`;
    })
    .sort()
    .join("|");
}

const FQHC_SOURCE_LABELS: Record<FqhcSourceProgram, string> = {
  hrsa_hc_sites: "HRSA health center sites",
  hrsa_uds_h80: "HRSA UDS Awardee",
  hrsa_uds_lal: "HRSA UDS Look-Alike",
  hrsa_chqr: "HRSA CHQR",
  hrsa_active_grants: "HRSA active grants",
  hrsa_ftca: "HRSA FTCA",
  hrsa_340b: "HRSA 340B",
};

export function buildFqhcSourceTooltip(
  meta: NetworkDashboardMeta | null,
  sources: FqhcSourceProgram[],
): string {
  if (!meta?.source_statuses) return "";
  return sources
    .map((source) => {
      const details = meta.source_statuses?.[source];
      if (!details) return null;
      return [
        `${FQHC_SOURCE_LABELS[source]} — Status: ${details.status}`,
        details.source_period ? `Period: ${details.source_period}` : null,
        details.source_as_of_date
          ? `As of: ${details.source_as_of_date}`
          : null,
      ]
        .filter((part): part is string => Boolean(part))
        .join(" · ");
    })
    .filter((line): line is string => Boolean(line))
    .join("\n");
}

function joinKpiDetails(parts: Array<string | null>): string {
  return parts.filter((part): part is string => Boolean(part)).join(" · ");
}

function countLabel(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function formatFqhcNumber(value: number | null): string {
  return value == null ? "—" : String(value);
}

export function formatFqhcPoints(value: number | null): string {
  if (value == null) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value} pts`;
}

export function formatFqhcPercent(value: number | null): string {
  return value == null ? "—" : `${value}%`;
}

export function formatFqhcCenterType(
  value: FqhcDashboardFacility["center_type"],
): string {
  if (value === "awardee") return "Awardee";
  if (value === "look_alike") return "Look-Alike";
  return "—";
}

export function formatFqhcFtcaStatus(
  value: FqhcDashboardFacility["ftca_status"],
): string {
  if (value === "deemed") return "Deemed";
  if (value === "lapsed") return "Lapsed";
  if (value === "not_found") return "Not found";
  if (value === "not_eligible") return "Not eligible";
  return "—";
}

export function formatFqhcFtcaStatusReason(
  value: FqhcDashboardFacility["ftca_status_reason"],
): string {
  if (value === "period_not_started") {
    return "FTCA deeming period has not started";
  }
  if (value === "source_unavailable") return "FTCA source unavailable";
  if (value === "status_unavailable") return "FTCA status unavailable";
  if (value === "look_alike") return "Look-Alikes are not FTCA eligible";
  return "";
}

export function formatFqhcRegistration(value: boolean | null): string {
  if (value == null) return "—";
  return value ? "Yes" : "No";
}

export function buildLiveFqhcDashboardModel(
  data: FqhcDashboardData,
  linkedFqhcs: FacilityHierarchyInput[],
): FqhcLiveDashboardModel {
  const linkedById = new Map<number, FacilityHierarchyInput>();
  linkedFqhcs.forEach((facility) => {
    const facilityId = getFacilityHierarchyId(facility);
    if (facilityId != null) linkedById.set(facilityId, facility);
  });

  const sourceFacilities =
    (data.facilities ?? []).length > 0
      ? data.facilities
      : linkedFqhcs.map((facility): FqhcDashboardFacility => {
          const facilityId = getFacilityHierarchyId(facility);
          return {
            facility_id: facilityId,
            ccn: null,
            health_center_key: null,
            bhcmis_id: null,
            identity_status: "unmatched",
            name: facility.facility_name ?? null,
            city: typeof facility.city === "string" ? facility.city : null,
            state: typeof facility.state === "string" ? facility.state : null,
            center_type: null,
            grant_or_designation_expires_on: null,
            expiration_availability_reason: null,
            chqr_badge_count: null,
            chqr_cycle_year: null,
            cqm_delta_points: null,
            cqm_reporting_year: null,
            cqm_measure_count: null,
            ftca_status: null,
            ftca_status_reason: null,
            ftca_expires_on: null,
            is_340b_registered: null,
            contract_pharmacy_count: null,
            active_site_count: null,
            missing_sources: ["ccn"],
          };
        });
  const roster = sourceFacilities.map((facility, index): FqhcLiveRosterRow => {
    const linked =
      facility.facility_id == null
        ? undefined
        : linkedById.get(facility.facility_id);
    return {
      ...facility,
      key:
        facility.facility_id != null
          ? `fqhc-live-${facility.facility_id}`
          : `fqhc-live-${facility.ccn ?? index}`,
      dashboardFacility:
        linked && facility.facility_id != null
          ? toDashboardFacility(linked, facility.facility_id)
          : null,
    };
  });

  const grants = data.kpis.grants_expiring_12m;
  const cqm = data.kpis.cqm_delta;
  const chqr = data.kpis.chqr;
  const ftca = data.kpis.ftca;
  const kpis: FqhcKpiTile[] = [
    {
      id: "grants",
      label: "Grants expiring in 12 months",
      value: formatFqhcNumber(grants.count),
      sublabel: joinKpiDetails([
        countLabel(grants.eligible_centers, "eligible center"),
        grants.earliest_expires_on
          ? `Earliest ${grants.earliest_expires_on}`
          : null,
        countLabel(grants.missing_date_count, "missing date"),
      ]),
      valueTone: grants.count > 0 ? "warn" : undefined,
    },
    {
      id: "cqm",
      label: "CQM benchmark delta",
      value: formatFqhcPoints(cqm.points),
      sublabel: joinKpiDetails([
        cqm.reporting_year == null ? null : String(cqm.reporting_year),
        countLabel(cqm.measure_count, "measure"),
        countLabel(cqm.contributing_centers, "contributing center"),
      ]),
    },
    {
      id: "chqr",
      label: "CHQR badges",
      value: formatFqhcNumber(chqr.badge_count),
      sublabel: joinKpiDetails([
        chqr.cycle_year == null ? null : String(chqr.cycle_year),
        `${chqr.centers_with_badges} of ${chqr.centers_in_scope} centers with badges`,
      ]),
    },
    {
      id: "ftca",
      label: "FTCA coverage",
      value: formatFqhcPercent(ftca.coverage_pct),
      sublabel: joinKpiDetails([
        `${ftca.deemed_count} of ${ftca.eligible_centers} eligible deemed`,
        countLabel(ftca.lapsed_count, "lapsed center"),
        ftca.earliest_expires_on
          ? `Earliest expiry ${ftca.earliest_expires_on}`
          : null,
      ]),
    },
  ];

  return {
    facilityCount: Math.max(data.total_count ?? 0, roster.length),
    kpis,
    roster,
  };
}

export function fqhcToneColor(tone: FqhcTone): string {
  if (tone === "error") return "#a4262c";
  if (tone === "warn") return "#8b4409";
  if (tone === "ok") return "#107c10";
  if (tone === "muted") return "#737373";
  return "#323130";
}

export function isFqhcTypeDashboard(typeCode: string | null | undefined): boolean {
  return String(typeCode || "").toUpperCase() === FQHC_TYPE_CODE;
}
